import LoadingSpinner from "@/components/LoadingSpinner";
import TeamSelectDeprecated from "@/components/messaging/TeamSelect";
import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import NetSuiteConfigTable from "./NetSuiteConfigTable";
import NetSuiteMappingTable from "./NetSuiteMappingTable";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import NetSuiteAccounts from "./NetSuiteAccounts";
import NetsuiteTabs from "./NetSuiteTabs";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import { Link, useNavigate } from "react-router-dom";
import SkillControlsPanel from "../SkillControlsPanel";
import IconArrowBack2 from "@/icons/IconArrowBack2";
import { IconClose1 } from "@velaro/velaro-component-lib";

interface NetSuiteConfig {
  id: number;
  displayName: string;
  company: string;
  consumerKey: string;
  consumerSecret: string;
  token: string;
  tokenSecret: string;
  restletUrl: string;
  teamId: number;
}

// ─── AI Tools ──────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  // Customer Intelligence
  {
    name: "netsuite_search_customer",
    label: "Search customers",
    description: "Search customers by email, name, phone, or company.",
    group: "Customer Intelligence"
  },
  {
    name: "netsuite_get_customer",
    label: "Full customer profile",
    description: "Full customer profile with balance, credit limit, terms.",
    group: "Customer Intelligence"
  },
  {
    name: "netsuite_get_customer_contacts",
    label: "Customer contacts",
    description: "List contacts for a company account.",
    group: "Customer Intelligence"
  },
  {
    name: "netsuite_get_customer_balance",
    label: "Customer balance",
    description: "Financial summary: balance, overdue, credit limit.",
    group: "Customer Intelligence"
  },
  {
    name: "netsuite_update_customer",
    label: "Update customer",
    description: "Update customer fields from conversation.",
    group: "Customer Intelligence"
  },
  // Order Management
  {
    name: "netsuite_search_orders",
    label: "Search orders",
    description: "Search orders by email, status, or date range.",
    group: "Order Management"
  },
  {
    name: "netsuite_get_order",
    label: "Order details",
    description: "Full order details with line items.",
    group: "Order Management"
  },
  {
    name: "netsuite_get_order_history",
    label: "Order history",
    description: "Recent orders for a customer.",
    group: "Order Management"
  },
  {
    name: "netsuite_cancel_order",
    label: "Cancel / hold order",
    description: "Cancel or hold an open order.",
    group: "Order Management"
  },
  {
    name: "netsuite_add_order_note",
    label: "Add order note",
    description: "Add memo to an order.",
    group: "Order Management"
  },
  // Invoicing & Payments
  {
    name: "netsuite_get_invoice",
    label: "Invoice details",
    description: "Invoice details with payment status.",
    group: "Invoicing & Payments"
  },
  {
    name: "netsuite_get_open_invoices",
    label: "Open invoices",
    description: "Unpaid invoices sorted by due date.",
    group: "Invoicing & Payments"
  },
  {
    name: "netsuite_get_payment_history",
    label: "Payment history",
    description: "Payment records for a customer.",
    group: "Invoicing & Payments"
  },
  {
    name: "netsuite_get_credit_memos",
    label: "Credit memos",
    description: "Credits and refund memos.",
    group: "Invoicing & Payments"
  },
  // Case Management
  {
    name: "netsuite_create_case",
    label: "Create support case",
    description: "Open a support case with priority.",
    group: "Case Management"
  },
  {
    name: "netsuite_get_cases",
    label: "Get cases",
    description: "Open cases for a customer.",
    group: "Case Management"
  },
  {
    name: "netsuite_update_case",
    label: "Update case",
    description: "Change case status or priority.",
    group: "Case Management"
  },
  {
    name: "netsuite_add_case_note",
    label: "Add case note",
    description: "Add note to existing case.",
    group: "Case Management"
  },
  // Returns & RMA
  {
    name: "netsuite_get_rma",
    label: "RMA status",
    description: "Return authorization status.",
    group: "Returns & RMA"
  },
  {
    name: "netsuite_create_rma",
    label: "Create RMA",
    description: "Create return from sales order.",
    group: "Returns & RMA"
  },
  // Inventory & Products
  {
    name: "netsuite_search_items",
    label: "Search products",
    description: "Search products by keyword or SKU.",
    group: "Inventory & Products"
  },
  {
    name: "netsuite_check_stock",
    label: "Check stock",
    description: "Inventory levels across locations.",
    group: "Inventory & Products"
  },
  {
    name: "netsuite_get_item_pricing",
    label: "Item pricing",
    description: "Base price, cost, online price.",
    group: "Inventory & Products"
  },
  {
    name: "netsuite_restlet",
    label: "Custom SuiteScript",
    description: "Custom SuiteScript for advanced operations.",
    group: "Inventory & Products"
  },
  {
    name: "netsuite_get_opportunity",
    label: "Get opportunity",
    group: "Sales & Opportunities",
    description: "Get a specific NetSuite opportunity record by ID. Returns stage, probability, expected amount, and close date — lets the AI provide deal status updates during sales conversations."
  },
  {
    name: "netsuite_search_opportunities",
    label: "Search opportunities",
    group: "Sales & Opportunities",
    description: "Search NetSuite opportunities by customer, stage, or date range. Returns a list of matching deals with status, amount, and owner."
  },
  {
    name: "netsuite_get_subsidiaries",
    label: "Get subsidiaries",
    group: "Customer Intelligence",
    description: "List all active subsidiaries (legal entities and locations) in NetSuite. Essential for multi-entity B2B environments where customers belong to a specific subsidiary."
  },
  {
    name: "netsuite_create_quote",
    label: "Create quote",
    group: "Invoicing & Payments",
    description: "Create a NetSuite estimate (quote) record for a customer with line items, pricing, and expiry. Returns the new quote number — close B2B sales directly from chat."
  },
  {
    name: "netsuite_get_credit_memo",
    label: "Get credit memo",
    group: "Invoicing & Payments",
    description: "Get details on a specific NetSuite credit memo by ID — amount, applied invoices, and status. Use to answer 'where is my credit?' during support conversations."
  }
];

const GROUP_ICONS: Record<string, string> = {
  "Customer Intelligence": "👤",
  "Order Management": "📦",
  "Invoicing & Payments": "🧾",
  "Case Management": "🎧",
  "Returns & RMA": "↩️",
  "Inventory & Products": "🏷️",
  "Sales & Opportunities": "🤝"
};

// ─── Workflow Method Comparison ───────────────────────────────────────────────

function WorkflowMethodCard() {
  return (
    <div className="bg-white rounded-2xl shadow-md mb-4">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
        <span className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center text-sm">⚡</span>
        <div>
          <div className="font-semibold text-slate-800 text-sm">Which workflow method should I use?</div>
          <div className="text-xs text-slate-500">AI Skills (recommended) vs HTTP Hooks (advanced)</div>
        </div>
      </div>

      <div className="px-6 pb-5 pt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* AI Skills */}
          <div className="rounded-xl border-2 border-green-200 bg-green-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 rounded-full bg-green-600 text-white text-[10px] font-bold uppercase tracking-wide">Recommended</span>
              <span className="font-semibold text-green-800 text-sm">AI Skills (netsuiteAI node)</span>
            </div>
            <p className="text-xs text-green-700 mb-3">
              The AI handles everything. No JSON payloads, no response parsing, no two-pass hooks. Add a <strong>NetSuite AI</strong> node to any workflow — the AI calls the right tools automatically based on what the visitor asks.
            </p>
            <div className="space-y-1.5 mb-3">
              {["Zero configuration — works immediately after connecting", "AI picks the right tool automatically", "Natural language: 'Check my order #12345'", "29 tools: search, get, create, update records", "Errors surfaced in the Logs tab with full context"].map(t => (
                <div key={t} className="flex items-start gap-1.5 text-xs text-green-700">
                  <svg viewBox="0 0 8 8" width="10" height="10" fill="none" className="flex-shrink-0 mt-0.5"><path d="M1 4L3 6L7 2" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  {t}
                </div>
              ))}
            </div>
            <div className="bg-white/60 rounded-lg p-2.5 text-[10px] text-green-800 border border-green-200">
              <strong>When to use:</strong> Always — unless you have existing SuiteScript hooks or need a very specific POST payload shape that the AI can't produce.
            </div>
          </div>

          {/* HTTP Hooks */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 rounded-full bg-slate-500 text-white text-[10px] font-bold uppercase tracking-wide">Advanced</span>
              <span className="font-semibold text-slate-700 text-sm">HTTP Hooks (httpRequest node)</span>
            </div>
            <p className="text-xs text-slate-600 mb-3">
              Your workflow calls Velaro's NetSuite HTTP endpoints directly via <strong>httpRequest</strong> nodes with a JSON payload. You control the exact fields sent and received. The hook fires once when the node executes.
            </p>
            <div className="space-y-1.5 mb-3">
              {["Full control over request/response shape", "Works with custom SuiteScript restlets", "Good for exact field mapping requirements", "Requires JSON payload configuration per node", "Errors visible in Integration Logs + NLog"].map(t => (
                <div key={t} className="flex items-start gap-1.5 text-xs text-slate-600">
                  <svg viewBox="0 0 8 8" width="10" height="10" fill="none" className="flex-shrink-0 mt-0.5"><circle cx="4" cy="4" r="3" stroke="#64748b" strokeWidth="1.2"/></svg>
                  {t}
                </div>
              ))}
            </div>
            <div className="bg-white/60 rounded-lg p-2.5 text-[10px] text-slate-700 border border-slate-200">
              <strong>Endpoint format:</strong> <code className="bg-slate-100 px-1 rounded">POST /NetSuite/hooks/{'{'}{'}'}{"action"}</code> with <code className="bg-slate-100 px-1 rounded">siteid</code> header + JSON body. See the <strong>Data Mapping → HTTP POST</strong> tab for field references.
            </div>
          </div>
        </div>

        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
          <strong>Both methods coexist.</strong> You can have an AI Skills workflow for conversational queries and a separate HTTP hook workflow for data push on conversation resolve — they don't interfere with each other.
        </div>
      </div>
    </div>
  );
}

// ─── How it all works ─────────────────────────────────────────────────────────

function HowItWorksCard() {
  return (
    <div className="bg-white rounded-2xl shadow-md p-6 mb-4">
      <div className="font-semibold text-slate-800 text-base mb-1">How NetSuite works in Velaro</div>
      <p className="text-xs text-slate-500 mb-4">Three steps — connect once, everything else is automatic.</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Step 1 */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-slate-800 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
            <span className="font-semibold text-slate-700 text-sm">Connect your account</span>
          </div>
          <p className="text-xs text-slate-500 mb-3">Enter your NetSuite OAuth credentials (Consumer Key, Token, and Restlet URL) from your Integration Record in NetSuite.</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span className="font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[10px]">Consumer Key</span>
              <span className="text-slate-400">from Integration Record</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span className="font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[10px]">Restlet URL</span>
              <span className="text-slate-400">from deployed SuiteScript</span>
            </div>
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>

        {/* Step 2 */}
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
            <span className="font-semibold text-green-800 text-sm">29 AI tools activate</span>
          </div>
          <p className="text-xs text-green-700 mb-3">Customer, order, invoice, case, RMA, and inventory tools activate immediately — no configuration needed.</p>
          <div className="grid grid-cols-2 gap-1">
            {["Customer search","Customer profile","Customer contacts","Customer balance","Update customer","Search orders","Order details","Order history","Cancel order","Add order note","Invoice details","Open invoices","Payment history","Credit memos","Create case","Get cases","Update case","Case note","RMA status","Create RMA","Search items","Check stock","Item pricing","Custom SuiteScript"].map((t) => (
              <div key={t} className="flex items-center gap-1 text-[10px] text-green-700">
                <svg viewBox="0 0 8 8" width="8" height="8" fill="none" className="flex-shrink-0">
                  <path d="M1 4L3 6L7 2" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {t}
              </div>
            ))}
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>

        {/* Step 3 */}
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-[var(--color-primary)] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
            <span className="font-semibold text-blue-800 text-sm">Data surfaces everywhere</span>
          </div>
          <p className="text-xs text-blue-700 mb-3">ERP data appears in 3 places automatically — no configuration.</p>
          <div className="space-y-2">
            <div className="bg-white rounded-lg p-2 border border-blue-100">
              <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">Agent sidebar</div>
              <div className="text-[10px] text-blue-600 mt-0.5">NetSuite tab → Customer account, open orders, invoices, and cases during live chat</div>
            </div>
            <div className="bg-white rounded-lg p-2 border border-blue-100">
              <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">AI bot conversations</div>
              <div className="text-[10px] text-blue-600 mt-0.5">Bot queries NetSuite in real time to answer order, billing, and support questions</div>
            </div>
            <div className="bg-white rounded-lg p-2 border border-blue-100">
              <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">HTTP POST variables</div>
              <div className="text-[10px] text-blue-600 mt-0.5">Field Reference tab → copy variable names to push NetSuite data to your CRM or ticketing system</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── AI Tools Panel ───────────────────────────────────────────────────────────

function AiToolsPanel({ configId, showWizard }: { configId: number; showWizard: () => void }) {
  const groups = Array.from(new Set(AI_TOOLS.map((t) => t.group)));

  return (
    <div className="bg-white rounded-2xl shadow-md p-6 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-semibold text-slate-800 text-lg flex items-center gap-2">
            AI Tools
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
              29 tools
            </span>
          </div>
          <div className="text-sm text-slate-500 mt-0.5">
            These tools are available to any AI workflow node on this site. The AI calls them automatically — no configuration required.
          </div>
        </div>
        <LaunchWizardButton onClick={showWizard} />
      </div>

      <div className="border-t border-slate-100 pt-4 space-y-5">
        {groups.map((group) => {
          const tools = AI_TOOLS.filter((t) => t.group === group);
          return (
            <div key={group}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-base">{GROUP_ICONS[group] ?? "🔧"}</span>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{group}</span>
                <span className="text-[10px] text-slate-400">({tools.length} tools)</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {tools.map((tool) => (
                  <div
                    key={tool.name}
                    className="flex gap-3 p-3 rounded-lg border border-green-200 bg-green-50"
                  >
                    <div className="mt-0.5 w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center bg-green-500">
                      <svg viewBox="0 0 10 10" width="8" height="8" fill="none" aria-hidden="true">
                        <path d="M1.5 5L3.5 7L8.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-700">{tool.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{tool.description}</div>
                      <code className="text-[10px] text-slate-400 mt-1 block">{tool.name}</code>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
          <strong>How the AI uses these tools:</strong> When a visitor asks "what's the status of my order?", the AI
          automatically calls <code className="bg-blue-100 px-1 rounded">netsuite_search_orders</code> with
          their email and replies in natural language — no workflow nodes to configure, no JSON payloads.
          Use the <strong>Launch Starter Wizard</strong> to create a pre-built workflow in seconds.
        </div>
      </div>

      <div className="mt-8 pt-8 border-t border-slate-200">
        <SkillControlsPanel integration="netsuite" skills={AI_TOOLS} />
      </div>
    </div>
  );
}

export default function NetSuite() {
  const api = useApi();
  const [configs, setConfigs] = useState<NetSuiteConfig[]>([]);
  const { teamId, updateTeamId } = useSelectedTeam();
  const [selectedConfigId, setSelectedConfigId] = useState<number>(0);
  const [showWizard, setShowWizard] = useState(false);

  async function handleSetup() {
    // This is the old code for using an oauth login form
    // Currently this times out after 3600 seconds, so we are implementing another option
    // const resp = await api.messaging.get("NetSuite/Auth/LoginUrl");
    // const data = await resp.json();
    // window.location.href = data.url;

    //New API needs created for setting up the application
    //API takes the key and secret for an integration user and saves to the db

    const response = await api.messaging.post("NetSuite/Auth/SaveCredentials", {
      tenantId: "tenantId",
      displayName: "displayName",
      company: "company",
      token: "token",
      tokenSecret: "tokenSecret"
    });

    const data = await response.json();
  }

  async function oauth() {
    const resp = await api.messaging.get("NetSuite/Auth/LoginUrl");
    const data = await resp.json();
    window.location.href = data.url;
  }

  const fetchConfigs = useCallback(async () => {
    const resp = await api.messaging.get(`NetSuite/Integrations/`);
    const data = await resp.json();
    setConfigs(data);
    if (data.length === 1) {
      setSelectedConfigId(data[0].id);
    }
  }, [api.messaging]);

  const addConfig = useCallback(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const deleteConfig = useCallback(
    async (id: number) => {
      setConfigs((oldState) => {
        return oldState.filter((x) => x.id != id);
      });
      await api.messaging.delete(`NetSuite/Integrations/${id}`);
    },
    [api.messaging]
  );

  const handleConfigChange = (
    id: number,
    field: keyof NetSuiteConfig,
    value: string
  ) => {
    setConfigs((prevConfigs) =>
      prevConfigs.map((config) =>
        config.id === id ? { ...config, [field]: value } : config
      )
    );
  };

  return (
    <div>
      {showWizard && (
        <StarterWorkflowWizard
          integration="netsuite"
          netsuiteConfigId={selectedConfigId || configs[0]?.id}
          onClose={() => setShowWizard(false)}
        />
      )}

      {/* How it all works — always visible */}
      <HowItWorksCard />

      {/* Method comparison drop-down */}
      <WorkflowMethodCard />

      {configs.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800 mb-0.5">
                Deploy the chat widget to your site
              </div>
              <div className="text-xs text-blue-600">
                Your NetSuite integration is connected — grab your embed code from Deployments to go live.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/chat_window" className="text-xs px-3 py-1.5 rounded-lg bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 transition">Chat Window</Link>
              <Link to="/deployments" className="text-xs px-3 py-1.5 rounded-lg bg-[var(--color-primary)] hover:opacity-90 text-white transition">Get Embed Code →</Link>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
            <i className="fa-solid fa-lightbulb mt-0.5 shrink-0" />
            <span>
              <strong>Tip:</strong> You can also create a <strong>Knowledge Base Q&A</strong> page widget
              from <Link to="/deployments" className="underline hover:text-blue-900">Deployments → Page Widgets</Link> for
              a lightweight, AI-only Q&A experience — no live agent needed.
            </span>
          </div>
        </div>
      )}

      <div className="mb-6">
        <NetSuiteAccounts
          configs={configs}
          deleteConfig={deleteConfig}
          addConfig={addConfig}
          teamId={teamId!}
          onChange={handleConfigChange}
          onSelectConfig={setSelectedConfigId}
        />
      </div>

      {/* Starter wizard call-to-action — shown once a config exists */}
      {configs.length > 0 && (
        <div className="bg-white rounded-2xl shadow-md p-5 mb-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-slate-800 text-sm mb-0.5">Ready to build your first workflow?</div>
            <div className="text-xs text-slate-500">
              The starter wizard creates a pre-configured workflow for order lookup, customer accounts, support cases, returns, billing, or ERP specialist orchestration — ready to customise.
            </div>
          </div>
          <LaunchWizardButton onClick={() => setShowWizard(true)} />
        </div>
      )}

      {/* AI Tools grid — shown once a config exists */}
      {configs.length > 0 && (
        <AiToolsPanel
          configId={selectedConfigId || configs[0]?.id}
          showWizard={() => setShowWizard(true)}
        />
      )}

      <div className="mb-4">
        <NetsuiteTabs teamId={teamId!} selectedConfigId={selectedConfigId} />
      </div>
    </div>
  );
}

export function BackToIntegrationLanding() {
  const navigate = useNavigate();
  return (
    <div
      className="flex cursor-pointer"
      onClick={() => navigate("/integrations")}
    >
      <IconClose1 className="h-6 w-6" />
    </div>
  );
}
